import { ReactElement, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CropFreeIcon from "@mui/icons-material/CropFree";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { UploadedDocumentsProps } from "./DocumentUpload";
import { useIsMobile } from "pages/landing";

// Set the workerSrc to properly load PDFs
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const DocumentPreview = (props: any): ReactElement => {
  const {
    showPdfViewer,
    onCloseDocument,
    activeDocument,
    loadDocumentsList,
  }: {
    showPdfViewer: boolean;
    onCloseDocument: () => void;
    activeDocument: UploadedDocumentsProps;
    loadDocumentsList: () => void;
  } = props;
  const {
    blobFilePath,
    blobFilePathToDownload,
  }: { blobFilePath: string; blobFilePathToDownload: string } = activeDocument;

  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.5);
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);

  // UI Actions
  const onDocumentLoadSuccess = ({ numPages }: any): void => {
    console.log("numPages", numPages);
    setNumPages(numPages);
    setPageNumber(pageNumber);
  };

  const zoomIn = (): any => setScale((prev: any) => prev + 0.2);
  const zoomOut = (): any => setScale((prev: any) => Math.max(prev - 0.2, 0.5));

  // Hooks
  useEffect(() => {
    if (numPages !== null) {
      setLoading(false);
    }
  }, []);

  // UI Elements
  return (
    <Dialog fullScreen open={showPdfViewer}>
      <>
        <Grid
          container
          className="toolbar-container justify-space px-3"
          spacing={3}
        >
          <Grid size={{ xs: 4 }}>
            <Typography variant="h3" className="document-name">
              {activeDocument.documents.documentName}
            </Typography>
          </Grid>
          <Grid size={{ xs: 4 }} className="justify-center">
            <Box className="flex align-center">
              <IconButton
                disableFocusRipple
                onClick={() => setPageNumber(pageNumber - 1)}
                disabled={pageNumber <= 1}
              >
                <KeyboardArrowLeftIcon className="toolbar-icon-button" />
              </IconButton>

              <Typography variant="h6" className="f14">
                <input
                  className="input-box"
                  type="number"
                  name="pageNumber"
                  value={!loading ? 0 : pageNumber}
                  onChange={(event: any) =>
                    setPageNumber(parseInt(event.target.value, 10))
                  }
                />{" "}
                / {numPages}
              </Typography>

              <IconButton
                disableFocusRipple
                onClick={() => setPageNumber(pageNumber + 1)}
                disabled={numPages === null || pageNumber >= numPages}
              >
                <KeyboardArrowRightIcon className="toolbar-icon-button" />
              </IconButton>

              <IconButton disableFocusRipple onClick={zoomIn}>
                <ZoomInIcon className="toolbar-icon-button " />
              </IconButton>
              <IconButton disableFocusRipple onClick={zoomOut}>
                <ZoomOutIcon className="toolbar-icon-button " />
              </IconButton>
              <IconButton disableFocusRipple onClick={() => setScale(1.5)}>
                <CropFreeIcon className="toolbar-icon-button" />
              </IconButton>
            </Box>
          </Grid>
          <Grid size={{ xs: 4 }} className="justify-end">
            <IconButton onClick={onCloseDocument}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Box className="document-preview-section">
          <DialogContent className="document-preview">
            <Grid container spacing={3} className="document-viewer-container">
              <Grid size={{ xs: 12 }} className="preview-panel ">
                {!loading && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}

                {loading && (
                  <Box className="align-content-center">
                    <Document
                      file={blobFilePathToDownload ?? blobFilePath}
                      onLoadSuccess={onDocumentLoadSuccess}
                      loading={<CircularProgress />}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={!isMobile ? undefined : window.innerWidth - 48}
                        scale={!isMobile ? scale : undefined}
                      />
                    </Document>
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
        </Box>
      </>
    </Dialog>
  );
};

export default DocumentPreview;
